import { Upload } from "lucide-react";
import { useState } from "react";
import { LoadingButton } from "../../../components/button/loading-button";
import {
  FileUpload,
  type PersistedUploadFile,
} from "../../../components/file-upload";
import { useToast } from "../../../contexts/toast/toast-context";
import api from "../../../services/api";
import type { Protocol, ProtocolAttachment } from "../../../types/protocol";
import { getApiErrorMessage } from "../../../utils";

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

type Props = {
  protocolId?: string;
  version?: number;
  readOnly?: boolean;
  attachments?: ProtocolAttachment[];
  selectedFiles: File[];
  onSelectedFilesChange: (files: File[]) => void;
  onProtocolChange?: (protocol: Protocol) => void;
  onMutationRejected?: () => void | Promise<void>;
};

export function ProtocolAttachmentsSection({
  protocolId,
  version,
  readOnly = false,
  attachments = [],
  selectedFiles,
  onSelectedFilesChange,
  onProtocolChange,
  onMutationRejected,
}: Props) {
  const { error, success } = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);

  const upload = async () => {
    if (readOnly || !protocolId || selectedFiles.length === 0 || isUploading)
      return;
    const formData = new FormData();
    selectedFiles.forEach((file) => formData.append("files", file));
    if (version !== undefined) formData.append("version", String(version));
    setIsUploading(true);
    try {
      const response = await api.post<Protocol>(
        `/protocols/${protocolId}/attachments`,
        formData,
      );
      onProtocolChange?.(response.data);
      onSelectedFilesChange([]);
      success("Anexo adicionado com sucesso.");
    } catch (err) {
      error(getApiErrorMessage(err));
      await onMutationRejected?.();
    } finally {
      setIsUploading(false);
    }
  };

  const remove = async (attachment: ProtocolAttachment) => {
    if (readOnly || !protocolId || deletingKey) return;
    setDeletingKey(attachment.key);
    try {
      const response = await api.delete<Protocol>(
        `/protocols/${protocolId}/attachments`,
        { data: { key: attachment.key, version } },
      );
      onProtocolChange?.(response.data);
      success("Anexo removido com sucesso.");
    } catch (err) {
      error(getApiErrorMessage(err));
      await onMutationRejected?.();
    } finally {
      setDeletingKey(null);
    }
  };

  return (
    <section className="app-card p-4 space-y-4">
      <div>
        <h2 className="app-section-title">Anexos</h2>
        <p className="mt-1 text-sm text-slate-500">
          Até 10 arquivos por envio, com limite de 5 MB por arquivo.
        </p>
      </div>
      <FileUpload
        files={selectedFiles}
        onFilesChange={onSelectedFilesChange}
        maxFiles={10}
        maxSizeBytes={MAX_FILE_SIZE_BYTES}
        acceptLabel="PDF, imagens, documentos e outros"
        persistedFiles={attachments.map((attachment) => ({
          id: attachment.key,
          name: attachment.fileName,
          size: attachment.size,
          type: attachment.contentType,
          url: attachment.url,
          date: attachment.createdAt,
        }))}
        onRemovePersisted={
          protocolId && !readOnly
            ? (file: PersistedUploadFile) => {
                const attachment = attachments.find(
                  (item) => item.key === file.id,
                );
                if (
                  attachment &&
                  window.confirm(`Excluir o anexo "${attachment.fileName}"?`)
                )
                  void remove(attachment);
              }
            : undefined
        }
        removingPersistedId={deletingKey}
        uploading={isUploading}
        disabled={readOnly}
        onValidationError={error}
      />
      <div className="flex justify-end">
        {protocolId && !readOnly && (
          <LoadingButton
            type="button"
            onClick={upload}
            isLoading={isUploading}
            loadingLabel="Enviando"
            disabled={selectedFiles.length === 0 || isUploading}
            className="btn btn-neutral cursor-pointer"
          >
            <Upload className="h-4 w-4" />
            Enviar
          </LoadingButton>
        )}
      </div>
      {readOnly && (
        <p className="text-sm text-slate-600">
          Protocolo encerrado: os anexos permanecem disponíveis somente para
          consulta.
        </p>
      )}
      {!protocolId && selectedFiles.length > 0 && (
        <p className="text-sm text-slate-600">
          {selectedFiles.length} arquivo(s) serão enviados ao registrar.
        </p>
      )}
    </section>
  );
}
